import { faCloudCheck } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import {
  faExclamationTriangle,
  faMicrophone,
  faSpinner,
  faSync,
} from '@fortawesome/free-solid-svg-icons';
import { Trans, useLingui } from '@lingui/react/macro';

import type { AppointmentPatient } from '../../react-query/useAppointments';
import type { ClinicalNote } from '../../react-query/videa-notes/useExistingNotes';
import { TooltipButton } from '../videaNotes/components/TooltipButton';
import type { VideaNotesAppointment } from '../videaNotes/helpers';
import type { VideaNotesAppointmentData } from '../videaNotes/types';
import { VideaNotesUIState, VoiceNotesSource } from '../videaNotes/types';

interface AmbientTableActionsProps {
  user: any;
  appointment: VideaNotesAppointment;
  notes: ClinicalNote[];
  hasWritebackSupport: boolean;
  isRecordingSessionActive: boolean;
  currentRecordingAppointment: VideaNotesAppointmentData | null;
  submitWriteback: {
    isLoading: boolean;
    mutate: (noteId: string, options?: any) => void;
  };
  openVideaNotes: (
    user: any,
    appointment: VideaNotesAppointment,
    mode: VideaNotesUIState,
    voiceNotesSource: VoiceNotesSource,
  ) => void;
  writeBackNote: (note: ClinicalNote) => void;
}

const getWritebackStatus = (
  note: ClinicalNote,
  patient?: AppointmentPatient,
) => {
  if (!patient?.pmsPatientId) return 'no-patient';
  if (!note.writebackStatus?.clinicalNoteId) return 'ready';

  const isProcessed = note.writebackStatus?.processedAt;
  const hasError = note.writebackStatus?.errorCode !== null;

  if (hasError) return 'error';
  if (note.writebackStatus.clinicalNoteId && !isProcessed) return 'inProgress';

  return 'synced';
};

const getWritebackIcon = (writebackStatus: string, isLoading: boolean) => {
  if (isLoading || writebackStatus === 'inProgress') {
    return faSpinner;
  }
  if (writebackStatus === 'synced') {
    return faCloudCheck;
  }
  if (writebackStatus === 'error' || writebackStatus === 'no-patient') {
    return faExclamationTriangle;
  }
  return faSync;
};

const getWritebackTooltip = (writebackStatus: string, t: any) => {
  switch (writebackStatus) {
    case 'synced':
      return t('Successfully synced');
    case 'error':
      return t('Sync issue - Click to retry');
    case 'no-patient':
      return t('Cannot sync this note. No matching patient found in your PMS.');
    case 'inProgress':
      return t('Syncing...');
    default:
      return t('Ready to sync - Click to start');
  }
};

export const AmbientTableActions = ({
  user,
  appointment,
  notes,
  hasWritebackSupport,
  isRecordingSessionActive,
  currentRecordingAppointment,
  submitWriteback,
  openVideaNotes,
  writeBackNote,
}: AmbientTableActionsProps) => {
  const { t } = useLingui();

  if (notes.length > 0) {
    const firstNote = notes[0];
    const writebackStatus = getWritebackStatus(firstNote, appointment.patient);

    return (
      <div className="vai-flex vai-gap-2 vai-flex-wrap">
        {/* View Notes Button */}
        <TooltipButton
          tooltip={t`View Notes`}
          variant="outline"
          size="sm"
          onClick={() =>
            openVideaNotes(
              user,
              appointment,
              VideaNotesUIState.SUMMARY_RESULT,
              VoiceNotesSource.VOICE_NOTES_TABLE,
            )
          }
        >
          <Trans>View</Trans>
        </TooltipButton>

        {/* Start Note Button */}
        <TooltipButton
          icon={faMicrophone}
          tooltip={t`Start Note`}
          variant="outline"
          size="sm"
          onClick={() =>
            openVideaNotes(
              user,
              appointment,
              VideaNotesUIState.AUDIO_CAPTURE,
              VoiceNotesSource.VOICE_NOTES_TABLE,
            )
          }
          disabled={
            isRecordingSessionActive &&
            currentRecordingAppointment?.unifiedId !==
              appointment.appointment.unifiedId
          }
        />

        {/* Writeback Status Button */}
        {hasWritebackSupport &&
          getWritebackStatus(firstNote, appointment.patient) !==
            'no-patient' && (
            <TooltipButton
              icon={getWritebackIcon(
                writebackStatus,
                submitWriteback.isLoading,
              )}
              tooltip={getWritebackTooltip(writebackStatus, t)}
              variant={
                writebackStatus === 'synced'
                  ? 'secondary'
                  : writebackStatus === 'error'
                    ? 'destructive'
                    : 'outline'
              }
              size="sm"
              className={
                writebackStatus === 'ready'
                  ? 'vai-bg-white hover:vai-bg-orange-50'
                  : writebackStatus === 'synced'
                    ? 'vai-bg-white vai-border vai-border-gray-300 hover:vai-bg-white cursor-default'
                    : ''
              }
              iconClassName={
                writebackStatus === 'ready'
                  ? 'vai-text-orange-500'
                  : writebackStatus === 'synced'
                    ? 'vai-text-green-600'
                    : ''
              }
              onClick={() => {
                if (
                  writebackStatus === 'ready' ||
                  writebackStatus === 'error'
                ) {
                  writeBackNote(firstNote);
                }
              }}
              disabled={
                submitWriteback.isLoading ||
                writebackStatus === 'inProgress' ||
                writebackStatus === 'no-patient'
              }
            />
          )}
      </div>
    );
  } else {
    // Show Start Note button for appointments without notes
    return (
      <TooltipButton
        icon={faMicrophone}
        tooltip={t`Start Note`}
        tooltipDisabled={true}
        size="sm"
        onClick={() =>
          openVideaNotes(
            user,
            appointment,
            VideaNotesUIState.AUDIO_CAPTURE,
            VoiceNotesSource.VOICE_NOTES_TABLE,
          )
        }
      >
        <Trans>Start Note</Trans>
      </TooltipButton>
    );
  }
};
